import { useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import type { SmsRecord } from './types';
import { formatBeijingDateTime } from '../../utils/time';

interface SmsRecordsTabProps {
  records: SmsRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  loading: boolean;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onSearch: (params: { phone?: string; status?: string; type?: string }) => void;
  onQueryRecord: (id: string) => Promise<void>;
}

const statusMap: Record<string, { label: string; className: string }> = {
  pending: { label: '待发送', className: 'bg-gray-50 text-gray-600' },
  sent: { label: '已提交', className: 'bg-green-50 text-green-600' },
  failed: { label: '发送失败', className: 'bg-red-50 text-red-600' },
};

const typeMap: Record<string, string> = {
  register: '注册验证码',
};

export default function SmsRecordsTab({
  records,
  pagination,
  loading,
  onRefresh,
  onPageChange,
  onSearch,
  onQueryRecord,
}: SmsRecordsTabProps) {
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('');
  const [queryingId, setQueryingId] = useState<string | null>(null);

  const handleSearch = () => {
    onSearch({ phone: phone || undefined, status: status || undefined });
  };

  const handleQuery = async (id: string) => {
    setQueryingId(id);
    try {
      await onQueryRecord(id);
    } finally {
      setQueryingId(null);
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">总记录</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">{pagination.total}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">当前页</p>
          <p className="text-xl lg:text-2xl font-bold text-light-text mt-2">{records.length}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">发送成功</p>
          <p className="text-xl lg:text-2xl font-bold text-green-600 mt-2">{records.filter((record) => record.status === 'sent').length}</p>
        </div>
        <div className="bg-white border border-light-border rounded-2xl p-4 lg:p-5 shadow-sm">
          <p className="text-xs lg:text-sm text-light-muted">发送失败</p>
          <p className="text-xl lg:text-2xl font-bold text-red-600 mt-2">{records.filter((record) => record.status === 'failed').length}</p>
        </div>
      </div>

      <div className="bg-white border border-light-border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-light-border">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-light-text">短信发送记录</h3>
            <p className="text-sm text-light-muted mt-1">查看验证码发送、防刷命中后的完整请求记录与 Spug 回执</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="搜索手机号"
              className="flex-1 px-3 py-2 border border-light-border rounded-xl text-sm focus:outline-none focus:border-accent-blue"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 border border-light-border rounded-xl text-sm focus:outline-none focus:border-accent-blue"
            >
              <option value="">全部状态</option>
              <option value="pending">待发送</option>
              <option value="sent">已提交</option>
              <option value="failed">发送失败</option>
            </select>
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 transition-colors text-sm"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">搜索</span>
            </button>
            <button
              onClick={onRefresh}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">刷新</span>
            </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-light-hover text-left text-sm text-light-muted">
                <th className="px-5 py-3 font-medium">手机号</th>
                <th className="px-5 py-3 font-medium">类型</th>
                <th className="px-5 py-3 font-medium">状态</th>
                <th className="px-5 py-3 font-medium">Request ID</th>
                <th className="px-5 py-3 font-medium">请求 IP</th>
                <th className="px-5 py-3 font-medium">响应</th>
                <th className="px-5 py-3 font-medium">使用</th>
                <th className="px-5 py-3 font-medium">时间</th>
                <th className="px-5 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-border">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-light-muted">加载中...</td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-light-muted">暂无短信记录</td>
                </tr>
              ) : records.map((record) => (
                <tr key={record.id} className="hover:bg-light-hover/50 transition-colors">
                  <td className="px-5 py-4 text-sm font-medium text-light-text">{record.phone}</td>
                  <td className="px-5 py-4 text-sm text-light-text">{typeMap[record.type] || record.type}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${statusMap[record.status]?.className || 'bg-gray-50 text-gray-600'}`}>
                      {statusMap[record.status]?.label || record.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs font-mono text-light-text max-w-[160px] truncate">{record.requestId || '-'}</td>
                  <td className="px-5 py-4 text-sm text-light-muted">{record.requestIp || '-'}</td>
                  <td className="px-5 py-4 text-sm text-light-text max-w-[220px]">
                    <div className="truncate" title={record.responseBody || record.queryBody || ''}>
                      {record.responseMessage || record.responseBody || '-'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-light-muted">{record.used ? '已使用' : '未使用'}</td>
                  <td className="px-5 py-4 text-sm text-light-muted whitespace-nowrap">{formatBeijingDateTime(record.createdAt)}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => handleQuery(record.id)}
                      disabled={!record.requestId || queryingId === record.id}
                      className="px-3 py-1.5 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs"
                    >
                      {queryingId === record.id ? '查询中' : '查回执'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="px-5 py-10 text-center text-light-muted">加载中...</div>
          ) : records.length === 0 ? (
            <div className="px-5 py-10 text-center text-light-muted">暂无短信记录</div>
          ) : (
            records.map((record) => (
              <div key={record.id} className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-light-text">{record.phone}</p>
                    <p className="text-xs text-light-muted">{typeMap[record.type] || record.type}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusMap[record.status]?.className || 'bg-gray-50 text-gray-600'}`}>
                    {statusMap[record.status]?.label || record.status}
                  </span>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-light-hover rounded-lg p-2">
                    <p className="text-xs text-light-muted">Request ID</p>
                    <p className="text-xs font-mono text-light-text truncate">{record.requestId || '-'}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2">
                    <p className="text-xs text-light-muted">请求 IP</p>
                    <p className="text-xs text-light-text">{record.requestIp || '-'}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2">
                    <p className="text-xs text-light-muted">使用状态</p>
                    <p className="text-xs text-light-text">{record.used ? '已使用' : '未使用'}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2">
                    <p className="text-xs text-light-muted">时间</p>
                    <p className="text-xs text-light-text">{formatBeijingDateTime(record.createdAt)}</p>
                  </div>
                </div>

                {/* Response */}
                {record.responseMessage || record.responseBody ? (
                  <div className="bg-light-hover rounded-lg p-2 mb-3">
                    <p className="text-xs text-light-muted mb-1">响应</p>
                    <p className="text-xs text-light-text line-clamp-2">{record.responseMessage || record.responseBody}</p>
                  </div>
                ) : null}

                {/* Action */}
                <button
                  onClick={() => handleQuery(record.id)}
                  disabled={!record.requestId || queryingId === record.id}
                  className="w-full px-3 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {queryingId === record.id ? '查询中...' : '查询回执'}
                </button>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-light-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-light-muted text-center sm:text-left">第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">上一页</span>
              </button>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-light-border hover:bg-light-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <span className="hidden sm:inline">下一页</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
